import {useState, useCallback} from 'react';
import {useIntl} from 'react-intl';
import {Dialog} from '@components/Core/company/modals/Dialog/DialogModal/DialogModal';
import {AppButton} from '@components/Core/buttons/AppButton/AppButton';
import {AppOutlinedButton} from '@components/Core/buttons/AppOutlinedButton/AppOutlinedButton';
import {CertificateList} from '../CertificateList/CertificateList';
import {HardwareTokenLoading} from '../HardwareTokenLoading/HardwareTokenLoading';
import {HardwareTokenError} from '../HardwareTokenError/HardwareTokenError';
import {HardwareTokenSuccess} from '../HardwareTokenSuccess/HardwareTokenSuccess';
import type {
    HardwareTokenCertificate,
    HardwareTokenDialogStep,
    HardwareTokenError as HardwareTokenErrorType
} from '../types';
import './HardwareTokenDialog.less';

interface HardwareTokenDialogProps {
    isOpen: boolean;
    onClose: () => void;
    certificates: HardwareTokenCertificate[];
    isCertificatesLoading: boolean;
    isConnecting: boolean;
    isSigning: boolean;
    error: HardwareTokenErrorType | null;
    isSuccess: boolean;
    onSign: (certificate: HardwareTokenCertificate) => void;
    onRetry: () => void;
}

function getDialogStep(props: {
    isConnecting: boolean;
    isCertificatesLoading: boolean;
    isSigning: boolean;
    error: HardwareTokenErrorType | null;
    isSuccess: boolean;
}): HardwareTokenDialogStep {
    const {
        isConnecting, isCertificatesLoading, isSigning, error, isSuccess
    } = props;

    if (error) {
        return 'error';
    }

    if (isSuccess) {
        return 'success';
    }

    if (isConnecting || isCertificatesLoading) {
        return 'connecting';
    }

    if (isSigning) {
        return 'signing';
    }

    return 'certificates';
}

/**
 * Main dialog component for hardware token signing flow.
 * Manages the signing workflow through multiple steps:
 * connecting, certificate selection, signing, success/error states.
 */
export function HardwareTokenDialog({
    isOpen,
    onClose,
    certificates,
    isCertificatesLoading,
    isConnecting,
    isSigning,
    error,
    isSuccess,
    onSign,
    onRetry
}: HardwareTokenDialogProps): JSX.Element {
    const {formatMessage} = useIntl();

    const [selectedCertificate, setSelectedCertificate] = useState<HardwareTokenCertificate | null>(null);

    const currentStep = getDialogStep({
        isConnecting,
        isCertificatesLoading,
        isSigning,
        error,
        isSuccess
    });

    const handleSelectCertificate = useCallback((certificate: HardwareTokenCertificate) => {
        setSelectedCertificate(certificate);
    }, []);

    const handleSign = useCallback(() => {
        if (selectedCertificate) {
            onSign(selectedCertificate);
        }
    }, [selectedCertificate, onSign]);

    const handleRetry = useCallback(() => {
        setSelectedCertificate(null);
        onRetry();
    }, [onRetry]);

    const handleClose = useCallback(() => {
        setSelectedCertificate(null);
        onClose();
    }, [onClose]);

    function renderContent(): JSX.Element {
        switch (currentStep) {
            case 'connecting':
                return <HardwareTokenLoading messageKey="hardwareToken.connecting" />;

            case 'signing':
                return <HardwareTokenLoading messageKey="hardwareToken.signing" />;

            case 'error':
                return error ? (
                    <HardwareTokenError
                        error={error}
                        onRetry={handleRetry}
                        onClose={handleClose}
                    />
                ) : <></>;

            case 'success':
                return <HardwareTokenSuccess onClose={handleClose} />;

            case 'certificates':
            default:
                return (
                    <CertificateList
                        certificates={certificates}
                        selectedCertificate={selectedCertificate}
                        onSelect={handleSelectCertificate}
                        isLoading={isCertificatesLoading}
                    />
                );
        }
    }

    function renderActions(): JSX.Element | null {
        if (currentStep !== 'certificates') {
            return null;
        }

        return (
            <div className="hardware-token-dialog-actions">
                <AppOutlinedButton
                    onClick={handleClose}
                    data-wdio="test-hardware-token-dialog-cancel"
                >
                    {formatMessage({id: 'oss.pcc.generic.cancel', defaultMessage: 'Cancel'})}
                </AppOutlinedButton>
                <AppButton
                    onClick={handleSign}
                    disabled={!selectedCertificate}
                    data-wdio="test-hardware-token-dialog-sign"
                >
                    {formatMessage({id: 'oss.pcc.generic.sign', defaultMessage: 'Sign'})}
                </AppButton>
            </div>
        );
    }

    const dialogTitle = currentStep === 'success' || currentStep === 'error'
        ? null
        : formatMessage({id: 'oss.pcc.CertificateList.title', defaultMessage: 'Certificate Selection'});

    return (
        <Dialog
            isOpen={isOpen}
            onClose={handleClose}
            className="hardware-token-dialog"
            title={dialogTitle}
            content={(
                <div className="hardware-token-dialog-content" data-wdio="test-hardware-token-dialog">
                    {renderContent()}
                </div>
            )}
            actions={renderActions()}
        />
    );
}
